// src/components/admin/feedback/FeedbackActionModal.jsx
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
  Paper
} from '@mui/material';
import {
  Edit,
  Add,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import { 
  updateChitalishte, 
  createChitalishte,
  updateInformationCard,
  createInformationCard
} from '../../../store/slices/chitalishteAdminSlice';
import { updateFeedbackStatus } from '../../../store/slices/adminFeedbackSlice';

const FeedbackActionModal = ({ open, onClose, feedback, chitalishte = null, onSuccess }) => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [actionType, setActionType] = useState(null); // 'update', 'create', 'delete'
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applyChanges, setApplyChanges] = useState(false);

  const steps = ['Избор на действие', 'Преглед на промени', 'Потвърждение'];

  const getRecommendedAction = () => {
    if (!feedback) return null;

    switch (feedback.feedbackType) {
      case 'location':
        return chitalishte ? 'update' : 'create';
      case 'basic_info':
        return chitalishte ? 'update' : 'create';
      case 'activity':
        return chitalishte ? 'update_card' : null;
      case 'website':
        return chitalishte ? 'update' : null;
      default:
        return null;
    }
  };

  const handleApplyChanges = async () => {
    if (!applyChanges) {
      await dispatch(updateFeedbackStatus({
        id: feedback.id,
        status: 'PROCESSED',
        processedBy: 'admin',
        notes: 'Прегледано без промени'
      }));
      onSuccess?.();
      onClose();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result;

      switch (actionType) {
        case 'update':
          result = await dispatch(updateChitalishte({
            id: chitalishte.id,
            data: formData,
            feedbackId: feedback.id
          })).unwrap();
          break;

        case 'create':
          result = await dispatch(createChitalishte({
            data: formData,
            feedbackId: feedback.id
          })).unwrap();
          break;

        case 'update_card':
          // За обновяване на информационна карта
          result = await dispatch(updateInformationCard({
            id: formData.id,
            data: formData,
            feedbackId: feedback.id
          })).unwrap();
          break;

        case 'create_card':
          result = await dispatch(createInformationCard({
            data: formData,
            feedbackId: feedback.id
          })).unwrap();
          break;

        default:
          throw new Error('Невалиден тип действие');
      }

      // Обнови статуса на feedback-а
      await dispatch(updateFeedbackStatus({
        id: feedback.id,
        status: 'PROCESSED',
        processedBy: 'admin',
        notes: `Приложени промени: ${actionType}`
      })).unwrap();

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Грешка при прилагане на промените');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Какво искате да направите с този сигнал?
            </Typography>
            
            {getRecommendedAction() && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Препоръчително действие: {getActionLabel(getRecommendedAction())}
              </Alert>
            )}

            <Stack spacing={2}>
              {chitalishte && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: actionType === 'update' ? '2px solid primary.main' : '1px solid',
                    borderColor: actionType === 'update' ? 'primary.main' : 'divider'
                  }}
                  onClick={() => setActionType('update')}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Edit color={actionType === 'update' ? 'primary' : 'action'} />
                    <Box>
                      <Typography variant="subtitle1">Обнови съществуващо читалище</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {chitalishte.name}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  border: actionType === 'create' ? '2px solid primary.main' : '1px solid',
                  borderColor: actionType === 'create' ? 'primary.main' : 'divider'
                }}
                onClick={() => setActionType('create')}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Add color={actionType === 'create' ? 'primary' : 'action'} />
                  <Box>
                    <Typography variant="subtitle1">Създай ново читалище</Typography>
                    <Typography variant="body2" color="text.secondary">
                      На база информацията от сигнала
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Преглед на промените
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              Моля, прегледайте внимателно информацията преди да приложите промените
            </Alert>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Информация от сигнала:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {feedback.message}
                </Typography>
              </Paper>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={applyChanges}
                  onChange={(e) => setApplyChanges(e.target.checked)}
                />
              }
              label="Искам да приложа тези промени в базата данни"
              sx={{ mt: 2 }}
            />
          </Box>
        );

      case 2:
        return (
          <Box textAlign="center">
            <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Готови ли сте?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {applyChanges 
                ? 'Промените ще бъдат приложени и сигналът ще бъде маркиран като обработен.'
                : 'Сигналът ще бъде маркиран като обработен без да се правят промени.'}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      update: 'Обнови съществуващо читалище',
      create: 'Създай ново читалище',
      update_card: 'Обнови информационна карта',
      create_card: 'Създай информационна карта'
    };
    return labels[action] || action;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Обработка на сигнал
        {feedback && (
          <Chip 
            label={feedback.feedbackType} 
            size="small" 
            sx={{ ml: 2 }}
          />
        )}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Отказ
        </Button>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep(activeStep - 1)}>
            Назад
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={activeStep === 0 && !actionType}
          >
            Напред
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleApplyChanges}
            disabled={loading}
          >
            {loading ? 'Прилагане...' : 'Приложи'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackActionModal;